import React, { useMemo, useState } from 'react';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import { FilterOp } from '@marageti/z4-sdk/lib/entity/Search';
import { SearchResult, SearchRequest } from '@marageti/z4-sdk/lib/entity';
import { format, isValid } from 'date-fns';
import { useParams } from 'react-router-dom';
import InvoiceView from './invoice-view';

const today = new Date();

const InvoiceContainer = () => {
  const apiClient = useApiClient();
  const { partnerId } = useParams();

  const [selectedMonth, setSelectedMonth] = useState<Date | null>(today);
  const formattedDate = format(selectedMonth || today, 'yyyy-MM');

  //  This Elasticsearch query specifies a date range with custom intervals.
  //  "/M" typically represents a monthly interval, so ElasticSearch will round the given date
  //  down to the start of the month for gte queries and up to the end of the month for lte queries.
  // value: [2023-10-20||/M, 2023-10-20||/M] = 2023-10-01T00:00:00.000Z to 2023-10-31T23:59:59.999Z
  const searchRequest: SearchRequest = {
    filterBy: [{
      field: 'partnerId',
      op: FilterOp.FilterOpTerm,
      value: [partnerId!],
    },
    {
      field: 'invoiceDate',
      op: FilterOp.FilterOpTerm,
      value: [`${formattedDate}`],
    },
    ],
    sortBy: [{ field: 'createdAt', order: 'asc' }],
    pagination: { limit: 10000, page: 1 },
  };

  const { data: salesRecordings, isLoading } = useFetch<SearchResult<SalesRecording>>(apiClient.salesRecordingsClient.postSalesRecordingSearch, [searchRequest], !!partnerId);

  const totalSales = useMemo(() => {
    if (salesRecordings && salesRecordings.hits.length > 0) {
      const ts = salesRecordings.hits.reduce((total, hit) => hit.price.amountUSD + total, 0);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(ts);
    }
    return undefined;
  }, [salesRecordings]);

  const totalCommission = useMemo(() => {
    if (salesRecordings && salesRecordings.hits.length > 0) {
      const tc = salesRecordings.hits.reduce((total, hit) => hit.commission.amountUSD + total, 0);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tc);
    }
    return undefined;
  }, [salesRecordings]);

  const onChangeMonth = (value: Date | null) => {
    if (value === null || isValid(value)) {
      setSelectedMonth(value);
    } else {
      setSelectedMonth(today);
    }
  };

  return (
    <InvoiceView
      isLoading={isLoading}
      onChangeMonth={onChangeMonth}
      sales={salesRecordings?.hits}
      selectedMonth={selectedMonth}
      totalCommission={totalCommission}
      totalSales={totalSales}
    />
  );
};

export default InvoiceContainer;
